import camelCase from 'lodash/camelCase'
import upperFirst from 'lodash/upperFirst'

// todo: consider moving this code into a library for better re-usability

export function fieldComponentName(field: CmsData) {
  return upperFirst(camelCase(`Formie ${field.__typename}`))
}

export function fieldVisible(field: CmsData) {
  return !['disabled', 'hidden'].includes(field.visibility)
}

export function fieldName(field: CmsData, parentField?: CmsData) {
  return `__${field.__typename}${parentField ? `__${parentField.handle}` : ''}__${field.handle}`
}

const labelPositionMap: Record<string, string> = {
  'verbb\\formie\\positions\\AboveInput': 'before',
  'verbb\\formie\\positions\\BelowInput': 'after',
  'verbb\\formie\\positions\\Hidden': 'hidden',
}

export function fieldLabelPosition(settings: CmsData, field: CmsData, fallback = 'before') {
  return labelPositionMap[field.labelPosition || settings.defaultLabelPosition] || fallback
}

const helpPositionMap: Record<string, string> = {
  'verbb\\formie\\positions\\AboveInput': 'before',
  'verbb\\formie\\positions\\BelowInput': 'after',
}

export function fieldHelpPosition(settings: CmsData, field: CmsData, fallback = 'before') {
  return helpPositionMap[field.instructionsPosition || settings.defaultInstructionsPosition] || fallback
}

// FormKit has no label-position/help-position props, so those are implemented as CSS classes on the
// FormKit outer section instead (assets/scss/formkit/_overrides.scss). This combines the two into the
// single :classes="{ outer: ... }" string each field needs to pass.
export function fieldOuterClasses(settings: CmsData, field: CmsData) {
  return `formie-label-position--${fieldLabelPosition(settings, field)} formie-help-position--${fieldHelpPosition(settings, field)}`
}

/** FormKit's array-of-tuples validation shape: each entry is [ruleName, ...args]. */
export type FormKitValidationRule = [rule: string, ...args: any[]]

/**
 * Assemble a field's validation array out of rules that may or may not apply, dropping the ones that do
 * not. Each field builds its list from a handful of optional Formie settings, so this keeps the call
 * sites to a single expression and hands FormKit the tuple shape its `validation` prop expects.
 */
export function fieldValidation(...rules: (FormKitValidationRule | undefined | false)[]): FormKitValidationRule[] {
  return rules.filter((rule): rule is FormKitValidationRule => Boolean(rule))
}

// FormKit's validation prop accepts an array-of-tuples shape (each tuple: [ruleName, ...args]) as an
// alternative to its pipe-string syntax; used here since it composes more naturally when building the
// array from several optional rules. A field with no rules is simply optional by default in FormKit.
export function fieldValidationArray(field: CmsData): FormKitValidationRule[] {
  return field.required ? [['required', 'trim']] : []
}

// FormKit's own min/max rules compare the numeric value, not string length, so a character-count limit
// needs the dedicated 'length' rule instead (length:min,max; 0 here leaves the minimum unconstrained).
export function fieldTextValidationArray(field: CmsData): FormKitValidationRule[] {
  return field.limit && field.limitType === 'characters' ? [['length', 0, field.limitAmount]] : []
}

export function fieldOptions(field: CmsData) {
  return Object.fromEntries(field.options.map(({ label, value }: CmsData) => [value, label]))
}

export function fieldDefaultOptions(field: CmsData) {
  return (field.options ?? []).filter((option: CmsData) => option.isDefault).map((option: CmsData) => option.value)
}

export function fieldSingleDefaultValue(field: CmsData) {
  const [defaultOption] = fieldDefaultOptions(field)
  if (defaultOption !== undefined) {
    return defaultOption
  }
  if (field.defaultValue === null || field.defaultValue === undefined) {
    return undefined
  }
  return field.defaultValue
}

export function fieldHiddenDefaultValue(field: CmsData) {
  switch (field.defaultOption) {
    case 'currentUrl':
      return useRoute().fullPath

    case 'currentUrlNoQueryString':
      return useRoute().path

    default:
      return field.defaultValue
  }
}
